"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const explode_1 = __importDefault(require("@turf/explode"));
const helpers_1 = require("@turf/helpers");
const rhumb_bearing_1 = __importDefault(require("@turf/rhumb-bearing"));
const rhumb_distance_1 = __importDefault(require("@turf/rhumb-distance"));
const transform_translate_1 = __importDefault(require("@turf/transform-translate"));
const extent_1 = require("ol/extent");
const GeoJSON_1 = __importDefault(require("ol/format/GeoJSON"));
const GeometryType_1 = __importDefault(require("ol/geom/GeometryType"));
const MultiPoint_1 = __importDefault(require("ol/geom/MultiPoint"));
const Polygon_1 = __importDefault(require("ol/geom/Polygon"));
const Draw_1 = __importDefault(require("ol/interaction/Draw"));
const proj_1 = require("ol/proj");
const rbush_1 = __importDefault(require("rbush"));
const react_1 = require("react");
const OLSnapProvider_1 = require("../openlayers/OLSnapProvider");
const useOLMap_1 = require("../openlayers/useOLMap");
const GeoJSONFormat = new GeoJSON_1.default({
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
});
const InternalGeoJSONFormat = new GeoJSON_1.default({
    dataProjection: 'EPSG:3857',
    featureProjection: 'EPSG:3857',
});
function DrawSquareInteraction({ onDraw }) {
    const map = useOLMap_1.useOLMap();
    const onDrawRef = react_1.useRef(onDraw);
    const snappableFeatures = react_1.useContext(OLSnapProvider_1.OLSnappableCollectionContext);
    const snappablePoints = react_1.useMemo(() => {
        const rbush = new rbush_1.default();
        return rbush;
    }, []);
    react_1.useEffect(() => {
        snappableFeatures.forEach(feature => {
            const points = getFeaturePoints(feature);
            feature.set('snapPoints', points);
            snappablePoints.load(points);
        });
        return () => {
            snappableFeatures.forEach(feature => {
                var _a;
                snappablePoints.remove((_a = feature.get('snapPoints')) !== null && _a !== void 0 ? _a : []);
                feature.set('snapPoints', undefined);
            });
        };
    }, [snappablePoints, snappableFeatures]);
    react_1.useEffect(() => {
        const onAdd = (event) => {
            const points = getFeaturePoints(event.element);
            event.element.set('snapPoints', points);
            snappablePoints.load(points);
        };
        snappableFeatures.on('add', onAdd);
        return () => {
            snappableFeatures.un('add', onAdd);
        };
    }, [snappablePoints, snappableFeatures]);
    react_1.useEffect(() => {
        const onRemove = (event) => {
            var _a;
            snappablePoints.remove((_a = event.element.get('snapPoints')) !== null && _a !== void 0 ? _a : []);
            event.element.set('snapPoints', undefined);
        };
        snappableFeatures.on('remove', onRemove);
        return () => {
            snappableFeatures.un('remove', onRemove);
        };
    }, [snappablePoints, snappableFeatures]);
    react_1.useEffect(() => {
        onDrawRef.current = onDraw;
    }, [onDraw]);
    const interaction = react_1.useMemo(() => new Draw_1.default({
        type: GeometryType_1.default.CIRCLE,
        geometryFunction: createGeometryFunction(map, snappablePoints),
    }), [map, snappablePoints]);
    react_1.useEffect(() => {
        const onDrawEnd = (event) => {
            onDrawRef.current(GeoJSONFormat.writeFeatureObject(event.feature));
        };
        interaction.on('drawend', onDrawEnd);
        return () => {
            interaction.un('drawend', onDrawEnd);
        };
    }, [interaction]);
    react_1.useEffect(() => {
        map.addInteraction(interaction);
        return () => {
            map.removeInteraction(interaction);
        };
    }, [map, interaction]);
    return null;
}
function createGeometryFunction(map, snappableFeatures) {
    return (coordinates, geometry) => {
        const [firstCorner, thirdCorner] = coordinates;
        if (!geometry) {
            geometry = new Polygon_1.default([]);
        }
        const currentViewRotation = map.getView().getRotation();
        const verticesToRotate = new MultiPoint_1.default([
            [thirdCorner[0], firstCorner[1]],
            [firstCorner[0], thirdCorner[1]],
        ]);
        const anchor = extent_1.getCenter(verticesToRotate.getExtent());
        verticesToRotate.rotate(2 * currentViewRotation, anchor);
        const secondCorner = snapIfPossible(verticesToRotate.getCoordinates()[0], map, snappableFeatures);
        const fourthCorner = snapIfPossible(verticesToRotate.getCoordinates()[1], map, snappableFeatures);
        const first = helpers_1.point(proj_1.toLonLat(firstCorner));
        const fourth = helpers_1.point(proj_1.toLonLat(fourthCorner));
        const d = rhumb_distance_1.default(first, fourth);
        const a = rhumb_bearing_1.default(first, fourth);
        const snapAdjustedThirdCorner = proj_1.fromLonLat(transform_translate_1.default(helpers_1.point(proj_1.toLonLat(secondCorner)), d, a).geometry.coordinates);
        geometry.setCoordinates([
            [firstCorner, secondCorner, snapAdjustedThirdCorner, fourthCorner, firstCorner],
        ]);
        return geometry;
    };
}
function snapIfPossible(coordinate, map, snappablePoints) {
    var _a;
    const pixel = map.getPixelFromCoordinate(coordinate);
    const lowerLeft = map.getCoordinateFromPixel([pixel[0] - 10, pixel[1] + 10]);
    const upperRight = map.getCoordinateFromPixel([pixel[0] + 10, pixel[1] - 10]);
    const [minX, minY, maxX, maxY] = extent_1.boundingExtent([lowerLeft, upperRight]);
    const result = snappablePoints.search({
        minX,
        maxX,
        minY,
        maxY,
    });
    const shortest = result.reduce((agg, point) => {
        const { shortest, distance } = agg;
        const thisPoint = [point.x, point.y];
        const thisDistance = pixelDistance(pixel, map.getPixelFromCoordinate(thisPoint));
        if (shortest === null) {
            return {
                shortest: thisPoint,
                distance: thisDistance,
            };
        }
        else if (distance > thisDistance) {
            return {
                shortest: thisPoint,
                distance: thisDistance,
            };
        }
        else {
            return agg;
        }
    }, { distance: Infinity, shortest: null });
    return (_a = shortest.shortest) !== null && _a !== void 0 ? _a : coordinate;
}
function pixelDistance(pixel, pixel2) {
    return Math.sqrt(Math.pow((pixel[0] - pixel2[0]), 2) + Math.pow((pixel[1] - pixel2[1]), 2));
}
function getFeaturePoints(feature) {
    const geoJsonFeature = InternalGeoJSONFormat.writeFeatureObject(feature);
    const points = explode_1.default(geoJsonFeature)
        .features.map(f => { var _a; return (_a = f.geometry) === null || _a === void 0 ? void 0 : _a.coordinates; })
        .filter((x) => !!x)
        .map(([x, y]) => ({ x, y, maxX: x, maxY: y, minX: x, minY: y }));
    return points;
}
exports.default = DrawSquareInteraction;
//# sourceMappingURL=DrawSquareInteraction.js.map